import { PageHero } from "@/components/PageHero";
import { PageSkeleton } from "@/components/Skeletons";
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { toast } from "sonner";
import {
    Activity, Heart, ArrowLeft, Loader2, Moon, AlertCircle,
    Frown, Sparkles, Users, CheckCircle2, RefreshCcw, Copy, Check,
} from "lucide-react";
import CommunityPartnersTeaser from "@/components/CommunityPartnersTeaser";
import { DailyWellnessCheckIn, WellnessTrendsAndCoach, CareForYou } from "@/components/WellnessHub";
import SeoHead from "@/components/SeoHead";
import { ToolShell } from "@/pages/Tools";
import DripWelcomeBanner from "@/components/DripWelcomeBanner";
import useDripReferral from "@/hooks/useDripReferral";

// Dimensions. `severity_direction`:
//   "high_is_bad"  → the higher the slider, the worse the caregiver's week
//                    (burnout, stress, grief, isolation)
//   "low_is_bad"   → the lower the slider, the worse the caregiver's week
//                    (sleep, self_care — these are protective factors)
const DIMENSIONS = [
    { key: "burnout",   label: "Burnout",   help: "How depleted you feel right now.",          icon: Activity,     color: "text-coral-dark",       severity_direction: "high_is_bad" },
    { key: "sleep",     label: "Sleep",     help: "How well you're sleeping this week.",       icon: Moon,         color: "text-stage_late-dark",  severity_direction: "low_is_bad"  },
    { key: "stress",    label: "Stress",    help: "How much your shoulders are up by your ears.", icon: AlertCircle, color: "text-yellow-dark",     severity_direction: "high_is_bad" },
    { key: "grief",     label: "Grief",     help: "Anticipatory or active. It's real either way.", icon: Frown,     color: "text-stage_middle-dark", severity_direction: "high_is_bad" },
    { key: "self_care", label: "Self-care", help: "How much you're refilling your own cup.",   icon: Sparkles,     color: "text-teal-dark",        severity_direction: "low_is_bad"  },
    { key: "isolation", label: "Isolation", help: "How alone this has felt this week.",        icon: Users,        color: "text-[#4C5551]",        severity_direction: "high_is_bad" },
];

const BAND_STYLES = {
    high: "bg-coral-light/50 text-coral-dark",
    moderate: "bg-yellow-soft/80 text-yellow-dark",
    low: "bg-stage_early/50 text-stage_early-dark",
    unknown: "bg-cream-dark/60 text-[#6E7A74]",
};

// Reflection copy — keyed by dimension. Rendered based on the caregiver's
// worst-scoring dimension so the result screen feels specific, not generic.
const REFLECTIONS = {
    burnout: {
        7: "You said burnout is high. That's a real signal — not a personal failing. Let's name one thing to lift off the pile this week.",
        9: "You marked burnout very high. Please treat this like a warning light — the person doing the caring needs care too.",
    },
    sleep: {
        3: "You said sleep is thin. Sleep debt bends every other number on this page — protecting it isn't vanity.",
        1: "You marked sleep as almost none. This one deserves attention before anything else this week.",
    },
    stress: {
        7: "You said stress is running high. Bodies keep that score even when we don't notice it.",
        9: "You marked stress as constant. That's not sustainable — let's name where it's coming from.",
    },
    grief: {
        7: "You said grief is heavy. Anticipatory grief while your person is still here is one of the least talked about, hardest parts of this.",
        9: "You marked grief very high. Please hear this: grieving now, while they're still here, is normal. And it's a lot to carry alone.",
    },
    self_care: {
        3: "You said self-care is running low. That's not vanity to fix — you're the whole engine.",
        1: "You marked self-care as barely any. Even 10 minutes to yourself today counts.",
    },
    isolation: {
        7: "You said isolation is high. Being alone with this is one of the hardest parts. Even one honest conversation this week helps.",
        9: "You marked isolation very high. Please don't wait until it feels 'bad enough' to reach out.",
    },
};

/**
 * Pick the caregiver's hardest dimension from THIS check-in and return the
 * matching reflection. Falls back to a warm "steady week" line if nothing
 * crossed the concerning threshold — we never render an empty section.
 */
function pickReflection(payload) {
    let worst = null;
    let worstSeverity = 0;   // 0-10, higher = worse regardless of direction
    for (const d of DIMENSIONS) {
        const v = payload[d.key];
        if (v === null || v === undefined || v === "") continue;
        const severity = d.severity_direction === "high_is_bad" ? Number(v) : (10 - Number(v));
        if (severity > worstSeverity) {
            worstSeverity = severity;
            worst = d;
        }
    }
    if (!worst || worstSeverity < 7) return null;
    // Find the closest keyed reflection at or below this severity.
    const table = REFLECTIONS[worst.key] || {};
    const rawValue = worst.severity_direction === "high_is_bad" ? Number(payload[worst.key]) : Number(payload[worst.key]);
    const keys = Object.keys(table).map(Number).sort((a, b) => a - b);
    let matched = null;
    if (worst.severity_direction === "high_is_bad") {
        // Highest key ≤ raw value.
        for (const k of keys) if (k <= rawValue) matched = k;
    } else {
        // Lowest key ≥ raw value.
        for (const k of keys.slice().reverse()) if (k >= rawValue) matched = k;
    }
    const copy = table[matched] || table[keys[keys.length - 1]];
    return { dimension: worst.key, label: worst.label, value: rawValue, copy };
}

function WellnessHubBlock() {
    const [k, setK] = useState(0);
    return (
        <div className="space-y-4" data-testid="wellness-hub-block">
            <DailyWellnessCheckIn onDone={() => setK(x => x + 1)} />
            <WellnessTrendsAndCoach refreshKey={k} />
        </div>
    );
}

export default function Wellness({ publicMode = false }) {
    const sid = getSessionId();
    // Day 28 (and any future) drip email that lands on /burnout.
    const drip = useDripReferral();
    const [state, setState] = useState(null);
    const [logs, setLogs] = useState([]);
    const [draft, setDraft] = useState({});
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    // `justSaved` holds the check-in payload the caregiver just submitted
    // + the derived reflection. When set, we hide the form and show the
    // result state so "nothing happens on save" is impossible.
    const [justSaved, setJustSaved] = useState(null);
    const resultRef = useRef(null);

    const load = async () => {
        if (publicMode) { setLoading(false); return; }
        try {
            const r = await api.get(`/wellness/${sid}`);
            setState(r.data);
            setDraft(r.data?.latest || {});
            // Full log history for the over-time trend graph.
            const lr = await api.get(`/wellness/${sid}/logs`).catch(() => ({ data: [] }));
            setLogs(Array.isArray(lr.data) ? lr.data : []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [sid]);

    const submit = async () => {
        if (!Object.values(draft).some(v => v !== null && v !== undefined && v !== "")) {
            toast("Move at least one slider so we know where you are.");
            return;
        }
        setSaving(true);
        try {
            const payload = { session_id: sid, note };
            DIMENSIONS.forEach(d => {
                const v = draft[d.key];
                payload[d.key] = (v === "" || v === null || v === undefined) ? null : Number(v);
            });
            let resp = null;
            if (!publicMode) {
                resp = await api.post(`/wellness/check-in`, payload);
            }
            const reflection = pickReflection(payload);
            const bandHint = worstBand(payload); // for tone selection on the Clarity Call card
            setJustSaved({
                payload,
                response: resp?.data || null,
                reflection,
                bandHint,
                note,
            });
            setNote("");
            if (!publicMode) {
                // Refresh 14-day snapshot data quietly for the "past" section.
                await load();
            }
            toast.success(publicMode ? "Check-in complete." : "Check-in saved.");
            // Scroll the result into view — the form is tall.
            setTimeout(() => {
                if (resultRef.current) {
                    resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }, 60);
        } catch (_e) {
            toast.error("Couldn't save your check-in. Try again in a moment.");
        } finally {
            setSaving(false);
        }
    };

    const takeAnother = () => { setJustSaved(null); };

    if (loading) {
        return <PageSkeleton cards={3} testid="wellness-loading" />;
    }

    const inner = (
        <div data-clarity-mask="true" className="max-w-3xl space-y-8 animate-fade-in-up">

            {/* -------- Day 28 (or weekly) drip landing banner --------
                Different framing from the behavior tracker: Day 28 asks
                "how are YOU holding up?" — the burnout check-in is the
                page for the caregiver, not the loved one. */}
            {drip.kind && !drip.dismissed && !publicMode && (
                <DripWelcomeBanner
                    kind={drip.kind}
                    testid="wellness-drip-banner"
                    message={
                        drip.kind === "day28"
                            ? "You've been at this for a month. This one's about YOU, not your loved one — three minutes to check in on how you're holding up."
                            : `Lane's weekly check-in. How are you holding up caring for ${drip.lovedOneName || "your loved one"}?`
                    }
                    onDismiss={drip.dismiss}
                />
            )}
            <PageHero
                kicker="Caregiver Wellness"
                kickerIcon={Heart}
                title={justSaved ? "Thank you for checking in." : "How are you, really?"}
                subtitle={justSaved
                    ? "Here's what you just told me — and one honest next step you can take with it."
                    : "No judgment. No score that follows you around. Just a quiet pulse on how you're holding up, so we can lighten the load when it gets heavy."}
            />

            {/* Wellness Hub — 30-second rotating daily check-in + trends + coach */}
            {!publicMode && <WellnessHubBlock />}

            {/* -------------------- POST-SAVE RESULT STATE -------------------- */}
            {justSaved && (
                <div ref={resultRef} className="space-y-6" data-testid="wellness-result">
                    {publicMode ? (
                        <section
                            data-testid="wellness-public-banner"
                            className="ml-card p-4 md:p-5 border-l-4 border-l-teal-dark bg-teal-soft/40"
                        >
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <CheckCircle2 size={20} strokeWidth={1.8} className="text-teal-dark shrink-0" />
                                    <p className="text-sm text-[#1F2624] max-w-xl">
                                        <strong>Check-in complete.</strong> Want to track this over time? Memory Lane saves
                                        every check-in so you can see your pattern and show your doctor.
                                    </p>
                                </div>
                                <Link to="/beta?src=wellness-check" className="ml-btn-primary shrink-0 text-sm" data-testid="wellness-create-account-btn">
                                    Create your account
                                </Link>
                            </div>
                        </section>
                    ) : (
                    <section
                        data-testid="wellness-saved-banner"
                        className="ml-card p-4 flex items-center gap-3 border-l-4 border-l-teal-dark bg-teal-soft/40"
                    >
                        <CheckCircle2 size={20} strokeWidth={1.8} className="text-teal-dark shrink-0" />
                        <p className="text-sm text-[#1F2624]">
                            <strong>Check-in saved.</strong> This one moment doesn&apos;t follow you around — it just helps us hold the line with you.
                        </p>
                    </section>
                    )}

                    {/* Snapshot of what they just logged */}
                    <section
                        data-testid="wellness-result-snapshot"
                        className="ml-card p-5 md:p-6"
                    >
                        <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">You just logged</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                            {DIMENSIONS.map(d => {
                                const v = justSaved.payload[d.key];
                                return (
                                    <div key={d.key} className="rounded-2xl px-4 py-3 bg-cream-light">
                                        <p className="text-xs uppercase tracking-wider font-semibold opacity-80 flex items-center gap-1.5">
                                            <d.icon size={12} strokeWidth={1.8} className={d.color} />
                                            {d.label}
                                        </p>
                                        <p className="mt-0.5 text-2xl font-heading font-medium">
                                            {v === null || v === undefined || v === "" ? "—" : v}
                                            <span className="text-sm opacity-70">/10</span>
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Reflection — picks the caregiver's hardest dimension */}
                    <section
                        data-testid={justSaved.reflection ? "wellness-reflection-detail" : "wellness-reflection-steady"}
                        className="ml-card p-5 md:p-6 bg-cream-light/60"
                    >
                        {justSaved.reflection ? (
                            <>
                                <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">
                                    A word about {justSaved.reflection.label.toLowerCase()}
                                </p>
                                <p className="mt-2 text-[17px] text-[#1F2624] leading-relaxed">
                                    {justSaved.reflection.copy}
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">
                                    A word about this week
                                </p>
                                <p className="mt-2 text-[17px] text-[#1F2624] leading-relaxed">
                                    You&apos;re holding a steady line. That&apos;s unglamorous, invisible work — and it counts. See you next week.
                                </p>
                            </>
                        )}
                    </section>

                    {/* Over-time trend — only when there's history to show. */}
                    {!publicMode && logs.length >= 2 && <WellnessTrend logs={logs} />}

                    {/* Share the check-in with another caregiver */}
                    <section className="ml-card p-4 md:p-5" data-testid="wellness-share-block">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-sm text-[#1F2624]">
                                <strong>Know another caregiver who needs this?</strong> Send them the check-in.
                            </p>
                            <CopyCheckinLinkButton />
                        </div>
                    </section>

                    {/* Clarity Call card removed (Jul 2026, founder decision):
                        the caregiver side gives — no call-scheduling asks. */}

                    <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                        {publicMode ? (
                            <Link
                                to="/beta?src=wellness-check"
                                data-testid="wellness-public-cta-bottom"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-teal-dark hover:text-teal transition"
                            >
                                Create your free account <ArrowLeft size={16} strokeWidth={1.8} className="rotate-180" />
                            </Link>
                        ) : (
                            <Link
                                to="/dashboard"
                                data-testid="wellness-back-to-dashboard"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-teal-dark hover:text-teal transition"
                            >
                                <ArrowLeft size={16} strokeWidth={1.8} />
                                Back to dashboard
                            </Link>
                        )}
                        <button
                            type="button"
                            onClick={takeAnother}
                            data-testid="wellness-restart"
                            className="ml-btn-secondary"
                        >
                            <RefreshCcw size={18} strokeWidth={1.6} /> Take another check-in
                        </button>
                    </div>
                </div>
            )}

            {/* -------------------- PRE-SAVE FORM STATE -------------------- */}
            {!justSaved && (
                <>
                    {/* Gentle reflection from the 14-day trend */}
                    {state?.reflection && (
                        <div data-testid="wellness-reflection" className="ml-card p-5 md:p-6 bg-stage_early/30 !border-stage_early/40">
                            <p className="text-sm uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Today&apos;s reflection</p>
                            <p className="text-lg text-[#1F2624] leading-relaxed">{state.reflection}</p>
                        </div>
                    )}

                    {/* Current 14-day snapshot */}
                    {!publicMode && state?.log_count_14d > 0 && (
                        <section>
                            <h2 className="text-xl font-heading font-medium text-[#1F2624] mb-3">
                                The last 14 days
                            </h2>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="wellness-snapshot">
                                {DIMENSIONS.map(d => {
                                    const band = state.bands?.[d.key] || "unknown";
                                    const avg = state.averages_14d?.[d.key];
                                    return (
                                        <div key={d.key} className={`rounded-2xl px-4 py-3 ${BAND_STYLES[band] || BAND_STYLES.unknown}`}>
                                            <p className="text-xs uppercase tracking-wider font-semibold opacity-80">{d.label}</p>
                                            <p className="mt-0.5 text-2xl font-heading font-medium">
                                                {avg === null || avg === undefined ? "—" : avg}<span className="text-sm opacity-70">/10</span>
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Over-time trend graph */}
                    {!publicMode && logs.length >= 2 && <WellnessTrend logs={logs} />}

                    <section className="ml-card p-5 md:p-7 bg-white">
                        <h2 className="text-2xl font-heading font-medium text-[#12776F]">A 1-minute check-in</h2>
                        <p className="text-sm text-[#6E7A74] mt-1">0 means &quot;barely any&quot;, 10 means &quot;as much as it gets&quot;. Skip any you don&apos;t know.</p>
                        <div className="mt-6 space-y-5" data-testid="wellness-form">
                            {DIMENSIONS.map(d => {
                                const v = draft[d.key];
                                return (
                                    <div key={d.key} className="grid grid-cols-1 sm:grid-cols-3 sm:items-center gap-3">
                                        <div className="flex items-center gap-2">
                                            <d.icon strokeWidth={1.6} size={18} className={d.color} />
                                            <div>
                                                <p className="font-medium text-[#1F2624]">{d.label}</p>
                                                <p className="text-xs text-[#6E7A74]">{d.help}</p>
                                            </div>
                                        </div>
                                        <div className="sm:col-span-2 flex items-center gap-3">
                                            <input
                                                type="range"
                                                min={0}
                                                max={10}
                                                step={1}
                                                value={v === null || v === undefined || v === "" ? 0 : v}
                                                onChange={e => setDraft(s => ({ ...s, [d.key]: Number(e.target.value) }))}
                                                data-testid={`wellness-slider-${d.key}`}
                                                className="flex-1 accent-teal-dark"
                                            />
                                            <span className="w-12 text-right font-medium text-[#1F2624]" data-testid={`wellness-value-${d.key}`}>
                                                {v === null || v === undefined || v === "" ? "—" : v}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <label className="block mt-6">
                            <span className="block text-sm font-medium text-[#4C5551] mb-1.5">Anything you want to put down (optional)</span>
                            <textarea
                                data-testid="wellness-note"
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                placeholder="What's heavy this week, or what helped."
                                className="w-full bg-white border border-cream-dark rounded-2xl px-4 py-3 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-teal-soft"
                            />
                        </label>
                        <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
                            {publicMode ? (
                                <span className="text-sm text-[#6E7A74]">Free — no account needed.</span>
                            ) : (
                                <Link to="/dashboard" className="text-sm text-[#6E7A74] inline-flex items-center gap-1.5 hover:text-[#4C5551] min-h-[44px]">
                                    <ArrowLeft size={14} /> Back to your dashboard
                                </Link>
                            )}
                            <button
                                type="button"
                                onClick={submit}
                                disabled={saving}
                                data-testid="wellness-submit"
                                className="px-6 py-3 rounded-2xl font-medium bg-teal-dark text-white hover:bg-teal disabled:opacity-60 inline-flex items-center gap-2"
                            >
                                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                                Save my check-in
                            </button>
                        </div>
                    </section>

                    {/* Community partners — caregiver self-care directory.
                        Lives under wellness because partner help is fundamentally a
                        burnout-mitigation concern, not a clinical resource. */}
                    {!publicMode && <CareForYou />}
                    {!publicMode && <CommunityPartnersTeaser />}
                </>
            )}
        </div>
    );

    if (publicMode) return <ToolShell>{inner}</ToolShell>;
    return inner;
}

/** Public /wellness-check route — same check-in, no auth, no saving. */
export function WellnessCheckPublic() {
    return (
        <>
            <SeoHead
                title="Free Caregiver Wellness Check-In — 1 Minute, No Signup | Memory Lane"
                description="A free 1-minute wellness check-in for dementia caregivers. Six honest sliders — burnout, sleep, stress, grief, self-care, isolation — with a personal reflection. No account needed."
                canonicalPath="/wellness-check"
                image="https://static.prod-images.emergentagent.com/jobs/61cce65a-cd47-4af4-b633-11b331870af1/images/25e5cbe5301119be292908e328eb3bfa4b6cde7908b9004bcd938efcf9311189.png"
                imageAlt="How are you, really? 1-minute caregiver wellness check — free, no signup"
            />
            <Wellness publicMode />
        </>
    );
}

/** Copies the PUBLIC check-in URL (works for both modes). */
function CopyCheckinLinkButton() {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(`${window.location.origin}/wellness-check`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast("Copy failed — the link is " + `${window.location.origin}/wellness-check`);
        }
    };
    return (
        <button type="button" onClick={copy} className="ml-btn-secondary text-sm !min-h-[40px] !py-2" data-testid="wellness-copy-link">
            {copied ? <><Check size={14} strokeWidth={2} /> Copied</> : <><Copy size={14} strokeWidth={1.8} /> Copy link</>}
        </button>
    );
}

/**
 * WellnessTrend — the over-time graph. One SVG line per dimension
 * (toggleable), plotted across the caregiver's check-in history.
 */
function WellnessTrend({ logs }) {
    const [activeKeys, setActiveKeys] = useState(() => new Set(["burnout", "stress"]));
    const points = [...logs].reverse(); // oldest → newest
    const shown = points.slice(-20);    // last 20 check-ins

    const toggle = (key) => {
        setActiveKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const W = 600, H = 160, PAD = 14;
    const xFor = (i) => shown.length === 1 ? W / 2 : PAD + (i * (W - PAD * 2)) / (shown.length - 1);
    const yFor = (v) => H - PAD - (Number(v) / 10) * (H - PAD * 2);

    const LINE_COLORS = {
        burnout: "#C05B4D", sleep: "#5A5AE0", stress: "#B26B00",
        grief: "#8A4E76", self_care: "#2C7A72", isolation: "#4C5551",
    };

    return (
        <section className="ml-card p-5 md:p-6" data-testid="wellness-trend">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-xl font-heading font-medium text-[#1F2624]">Your check-ins over time</h2>
                <span className="text-xs text-[#6E7A74]">{shown.length} of {points.length} check-ins</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
                {DIMENSIONS.map(d => {
                    const on = activeKeys.has(d.key);
                    return (
                        <button
                            key={d.key}
                            type="button"
                            onClick={() => toggle(d.key)}
                            data-testid={`trend-toggle-${d.key}`}
                            className={`inline-flex items-center gap-1.5 rounded-2xl border px-3.5 py-1 min-h-[44px] text-xs font-medium transition-colors ${on ? "text-white border-transparent" : "bg-white border-cream-dark text-[#4C5551]"}`}
                            style={on ? { backgroundColor: LINE_COLORS[d.key] } : {}}
                        >
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: on ? "#fff" : LINE_COLORS[d.key] }} />
                            {d.label}
                        </button>
                    );
                })}
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="mt-4 w-full h-auto" role="img" aria-label="Wellness scores over time" data-testid="wellness-trend-svg">
                {[0, 5, 10].map(v => (
                    <g key={v}>
                        <line x1={PAD} x2={W - PAD} y1={yFor(v)} y2={yFor(v)} stroke="#E8E3D9" strokeWidth="1" />
                        <text x={0} y={yFor(v) + 4} fontSize="10" fill="#9CA6A1">{v}</text>
                    </g>
                ))}
                {DIMENSIONS.filter(d => activeKeys.has(d.key)).map(d => {
                    const pts = shown
                        .map((log, i) => ({ i, v: log[d.key] }))
                        .filter(p => p.v !== null && p.v !== undefined && p.v !== "");
                    if (pts.length < 1) return null;
                    const path = pts.map((p, j) => `${j === 0 ? "M" : "L"} ${xFor(p.i).toFixed(1)} ${yFor(p.v).toFixed(1)}`).join(" ");
                    return (
                        <g key={d.key}>
                            <path d={path} fill="none" stroke={LINE_COLORS[d.key]} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
                            {pts.map(p => (
                                <circle key={p.i} cx={xFor(p.i)} cy={yFor(p.v)} r="3.5" fill={LINE_COLORS[d.key]} />
                            ))}
                        </g>
                    );
                })}
            </svg>
            <p className="mt-2 text-xs text-[#6E7A74]">
                Tap a dimension to show or hide its line. Higher isn&apos;t always better — for sleep and self-care, higher is good; for the rest, lower is the win.
            </p>
        </section>
    );
}

/**
 * Compute a rough "band" for the check-in the caregiver just logged so the
 * Clarity Call card can pick the warm (coral) tone for hard weeks and the
 * default (teal) tone for steady ones. Any single dimension ≥ concern
 * threshold flips to "high".
 */
function worstBand(payload) {
    let worst = 0;
    for (const d of DIMENSIONS) {
        const v = payload[d.key];
        if (v === null || v === undefined || v === "") continue;
        const severity = d.severity_direction === "high_is_bad" ? Number(v) : (10 - Number(v));
        if (severity > worst) worst = severity;
    }
    if (worst >= 8) return "high";
    if (worst >= 5) return "moderate";
    return "low";
}
